import { useEffect, useState } from 'preact/hooks';
import { SKILLS, KEYSTONES, WEAPONS, ARMORS, CHARMS, CONSUMABLES, MOBS, PETS, HERO_IDS, type AdminCmd, type Modifier } from '@knight/shared';
import * as S from '../app/state';
import { t, tn } from '../i18n';
import { Btn, copyText } from './common';
import { game } from '../game/client';

/** Owner-only debug console: everything the sim and the account can do, behind allowlist + password. */

type Tab = 'self' | 'give' | 'world' | 'players' | 'account';

const CHEAT_MODS: { id: string; label: string; mods: Modifier[] }[] = [
  { id: 'speed', label: 'admin.cheat.speed', mods: [{ stat: 'moveSpeed', inc: 1.0 }] },
  { id: 'damage', label: 'admin.cheat.damage', mods: [{ stat: 'damage', more: 5 }] },
  { id: 'cooldown', label: 'admin.cheat.cooldown', mods: [{ stat: 'cooldown', more: 0.1 }] },
  { id: 'aspd', label: 'admin.cheat.aspd', mods: [{ stat: 'attackSpeed', more: 3 }] },
  { id: 'area', label: 'admin.cheat.area', mods: [{ stat: 'area', more: 2 }] },
  { id: 'summons', label: 'admin.cheat.summons', mods: [{ stat: 'summonCount', add: 5 }, { stat: 'summonPower', more: 2 }] },
  { id: 'tank', label: 'admin.cheat.tank', mods: [{ stat: 'maxHp', more: 5 }, { stat: 'armor', add: 200 }] },
  { id: 'luck', label: 'admin.cheat.luck', mods: [{ stat: 'luck', add: 10 }, { stat: 'xpGain', more: 5 }] },
];

const MOB_IDS = Object.keys(MOBS).filter((id) => !MOBS[id].pet);
const ITEM_IDS = [...Object.keys(WEAPONS), ...Object.keys(ARMORS), ...Object.keys(CHARMS)];
const SKILL_IDS = Object.keys(SKILLS);

function send(cmd: AdminCmd): void {
  game.send({ t: 'admin', cmd });
}

function Num(props: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; width?: number }) {
  return <input class="adm-num" type="number" value={props.value} min={props.min} max={props.max} step={props.step ?? 1} style={{ width: props.width ?? 70 }} onInput={(e) => props.onChange(Number((e.currentTarget as HTMLInputElement).value))} />;
}

function Sel(props: { value: string; options: { id: string; label: string }[]; onChange: (v: string) => void; width?: number }) {
  return (
    <select class="adm-sel" value={props.value} style={{ width: props.width ?? 170 }} onChange={(e) => props.onChange((e.currentTarget as HTMLSelectElement).value)}>
      {props.options.map((o) => (
        <option value={o.id}>{o.label}</option>
      ))}
    </select>
  );
}

export function AdminPanel() {
  const adm = S.admin.value;
  const acc = S.account.value;
  const offline = S.conn.value !== 'online';
  const inGame = S.inGame.value;
  const [tab, setTab] = useState<Tab>('self');
  const [password, setPassword] = useState('');
  const [target, setTarget] = useState<number | undefined>(undefined);
  const [cheats, setCheats] = useState<string[]>([]);
  const [level, setLevel] = useState(10);
  const [rank, setRank] = useState(10);
  const [skill, setSkill] = useState(SKILL_IDS[0]);
  const [skillLevel, setSkillLevel] = useState(1);
  const [aspect, setAspect] = useState('');
  const [keystone, setKeystone] = useState(Object.keys(KEYSTONES)[0]);
  const [item, setItem] = useState(ITEM_IDS[0]);
  const [rarity, setRarity] = useState(2);
  const [cons, setCons] = useState(Object.keys(CONSUMABLES)[0]);
  const [consN, setConsN] = useState(5);
  const [mob, setMob] = useState('ghoul');
  const [mobN, setMobN] = useState(5);
  const [elite, setElite] = useState(false);
  const [corruption, setCorruption] = useState(50);
  const [sayText, setSayText] = useState('');
  const [sayAs, setSayAs] = useState<number | undefined>(undefined);
  const [announce, setAnnounce] = useState('');
  const [fame, setFame] = useState(1000);
  const [unlockHero, setUnlockHero] = useState('all');
  const [slots, setSlots] = useState(6);
  const [pet, setPet] = useState(Object.keys(PETS)[0]);
  const [petLevel, setPetLevel] = useState(10);

  // offline play is the owner's own machine: the console is always available there
  useEffect(() => {
    if (offline && !adm.enabled) game.send({ t: 'admin.login', password: '' });
  }, [offline, adm.enabled]);
  useEffect(() => {
    if (adm.enabled && inGame) send({ k: 'players' });
  }, [adm.enabled, inGame, tab]);

  const tgt = target !== undefined ? { target } : {};
  const players = adm.players;
  const targetName = target === undefined ? t('admin.me') : players.find((p) => p.id === target)?.name ?? `#${target}`;
  const aspects = SKILLS[skill]?.aspects ?? [];

  function toggleCheat(id: string) {
    const next = cheats.includes(id) ? cheats.filter((c) => c !== id) : [...cheats, id];
    setCheats(next);
    send({ k: 'mods', mods: CHEAT_MODS.filter((c) => next.includes(c.id)).flatMap((c) => c.mods), ...tgt });
  }

  return (
    <div class="admin panel">
      <div class="row">
        <b class="gold">{t('admin.title')}</b>
        <span class={'badge ' + (adm.enabled ? 'on' : 'off')}>{adm.enabled ? t('admin.on') : t('admin.off')}</span>
        <div class="grow" />
        <Btn small onClick={() => (S.adminOpen.value = false)}>
          ✕
        </Btn>
      </div>
      <div class="small muted row" style={{ gap: 6 }}>
        <span>
          {t('admin.accountId')}: <code>{acc?.id ?? '—'}</code>
        </span>
        <Btn
          small
          ghost
          onClick={() => {
            void copyText(acc?.id ?? '');
            S.showToast(t('toast.copied'));
          }}
        >
          {t('admin.copy')}
        </Btn>
      </div>
      {!adm.enabled && !offline && (
        <div class="row">
          <input class="adm-text" type="password" placeholder={t('admin.password')} value={password} onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)} onKeyDown={(e) => e.key === 'Enter' && game.send({ t: 'admin.login', password })} />
          <Btn small primary onClick={() => game.send({ t: 'admin.login', password })}>
            {t('admin.login')}
          </Btn>
        </div>
      )}
      {!adm.enabled && <div class="small muted">{t('admin.hint')}</div>}
      {adm.enabled && (
        <>
          <div class="adm-tabs">
            {(['self', 'give', 'world', 'players', 'account'] as Tab[]).map((k) => (
              <button class={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                {t('admin.tab.' + k)}
              </button>
            ))}
          </div>
          {(tab === 'self' || tab === 'give') && (
            <div class="row small">
              <span class="muted">{t('admin.target')}:</span>
              <Sel
                value={target === undefined ? 'me' : String(target)}
                options={[{ id: 'me', label: t('admin.me') }, ...players.map((p) => ({ id: String(p.id), label: `${p.name} · ${tn('hero', p.hero)} ${p.level}` }))]}
                onChange={(v) => setTarget(v === 'me' ? undefined : Number(v))}
              />
            </div>
          )}
          {!inGame && tab !== 'account' && <div class="small blood">{t('admin.needGame')}</div>}

          {tab === 'self' && (
            <div class="col" style={{ gap: 8 }}>
              <div class="row wrap">
                <Btn small onClick={() => send({ k: 'god', on: true, ...tgt })}>
                  {t('admin.godOn')}
                </Btn>
                <Btn small onClick={() => send({ k: 'god', on: false, ...tgt })}>
                  {t('admin.godOff')}
                </Btn>
                <Btn small onClick={() => send({ k: 'heal', ...tgt })}>
                  {t('admin.heal')}
                </Btn>
                <Btn small danger onClick={() => target !== undefined && send({ k: 'slay', target })} disabled={target === undefined}>
                  {t('admin.slay')}
                </Btn>
              </div>
              <div class="row wrap">
                <span class="small muted">{t('label.level')}</span>
                <Num value={level} onChange={setLevel} min={1} max={20} />
                <Btn small onClick={() => send({ k: 'level', level, ...tgt })}>
                  {t('admin.setLevel')}
                </Btn>
                <Btn small onClick={() => send({ k: 'xp', amount: 500, ...tgt })}>
                  +500 XP
                </Btn>
                <Btn small onClick={() => send({ k: 'xp', amount: 5000, ...tgt })}>
                  +5000 XP
                </Btn>
              </div>
              <div class="row wrap">
                <span class="small muted">{t('label.rankTrack')}</span>
                <Num value={rank} onChange={setRank} min={1} max={20} />
                <Btn small onClick={() => send({ k: 'rank', rank, ...tgt })}>
                  {t('admin.setRank')}
                </Btn>
              </div>
              <div class="small muted">{t('admin.cheats')} · {targetName}</div>
              <div class="row wrap">
                {CHEAT_MODS.map((c) => (
                  <Btn small primary={cheats.includes(c.id)} onClick={() => toggleCheat(c.id)}>
                    {t(c.label)}
                  </Btn>
                ))}
                <Btn
                  small
                  ghost
                  onClick={() => {
                    setCheats([]);
                    send({ k: 'mods', mods: [], ...tgt });
                  }}
                >
                  {t('admin.reset')}
                </Btn>
              </div>
            </div>
          )}

          {tab === 'give' && (
            <div class="col" style={{ gap: 8 }}>
              <div class="row wrap">
                <Sel
                  value={skill}
                  options={SKILL_IDS.map((id) => ({ id, label: `${tn('skill', id)} (${SKILLS[id].kind})` }))}
                  onChange={(v) => {
                    setSkill(v);
                    setAspect('');
                  }}
                />
                <Num value={skillLevel} onChange={setSkillLevel} min={1} max={5} width={50} />
                {aspects.length > 0 && <Sel width={130} value={aspect} options={[{ id: '', label: '—' }, ...aspects.map((a) => ({ id: a.id, label: tn('aspect', a.id) }))]} onChange={setAspect} />}
                <Btn small onClick={() => send({ k: 'skill', id: skill, level: skillLevel, aspect: aspect || undefined, ...tgt })}>
                  {t('admin.give')}
                </Btn>
              </div>
              <div class="row wrap">
                <Sel value={keystone} options={Object.keys(KEYSTONES).map((id) => ({ id, label: tn('keystone', id) }))} onChange={setKeystone} />
                <Btn small onClick={() => send({ k: 'keystone', id: keystone, ...tgt })}>
                  {t('admin.give')}
                </Btn>
              </div>
              <div class="row wrap">
                <Sel value={item} options={ITEM_IDS.map((id) => ({ id, label: tn('item', id) }))} onChange={setItem} />
                <Sel width={110} value={String(rarity)} options={[0, 1, 2].map((r) => ({ id: String(r), label: t('label.rarity' + r) }))} onChange={(v) => setRarity(Number(v))} />
                <Btn small onClick={() => send({ k: 'item', def: item, rarity, ...tgt })}>
                  {t('admin.give')}
                </Btn>
              </div>
              <div class="row wrap">
                <Sel value={cons} options={Object.keys(CONSUMABLES).map((id) => ({ id, label: tn('cons', id) }))} onChange={setCons} />
                <Num value={consN} onChange={setConsN} min={1} max={5} width={50} />
                <Btn small onClick={() => send({ k: 'consumable', id: cons, n: consN, ...tgt })}>
                  {t('admin.give')}
                </Btn>
              </div>
            </div>
          )}

          {tab === 'world' && (
            <div class="col" style={{ gap: 8 }}>
              {adm.realm && (
                <div class="small muted">
                  {adm.realm.code} · {t('label.corruption')} {Math.round(adm.realm.corruption)}% · {adm.realm.phase} · {t('admin.mobs')} {adm.realm.mobs}
                  {adm.realm.boss ? ` · ${tn('mob', adm.realm.boss)}` : ''}
                </div>
              )}
              <div class="row wrap">
                <Sel value={mob} options={MOB_IDS.map((id) => ({ id, label: tn('mob', id) }))} onChange={setMob} />
                <Num value={mobN} onChange={setMobN} min={1} max={40} width={50} />
                <label class="small row" style={{ gap: 4 }}>
                  <input type="checkbox" checked={elite} onChange={(e) => setElite((e.currentTarget as HTMLInputElement).checked)} /> {t('admin.elite')}
                </label>
                <Btn small onClick={() => send({ k: 'spawn', def: mob, count: mobN, elite })}>
                  {t('admin.spawn')}
                </Btn>
              </div>
              <div class="row wrap">
                <Btn small onClick={() => send({ k: 'boss' })}>
                  {t('admin.boss')}
                </Btn>
                <Btn small danger onClick={() => send({ k: 'kill', radius: 6000 })}>
                  {t('admin.killAll')}
                </Btn>
                <Btn small onClick={() => send({ k: 'clear' })}>
                  {t('admin.clear')}
                </Btn>
                <Btn small onClick={() => send({ k: 'players' })}>
                  {t('admin.refresh')}
                </Btn>
              </div>
              <div class="row wrap">
                <span class="small muted">{t('label.corruption')}</span>
                <Num value={corruption} onChange={setCorruption} min={0} max={100} />
                <Btn small onClick={() => send({ k: 'corruption', value: corruption })}>
                  {t('admin.set')}
                </Btn>
              </div>
              <div class="row wrap">
                <input class="adm-text grow" placeholder={t('admin.announceText')} value={announce} onInput={(e) => setAnnounce((e.currentTarget as HTMLInputElement).value)} />
                <Btn small disabled={!announce.trim()} onClick={() => send({ k: 'announce', text: announce.trim() })}>
                  {t('admin.announce')}
                </Btn>
              </div>
            </div>
          )}

          {tab === 'players' && (
            <div class="col" style={{ gap: 8 }}>
              <div class="row">
                <Btn small onClick={() => send({ k: 'players' })}>
                  {t('admin.refresh')}
                </Btn>
                <span class="small muted">{players.length}</span>
              </div>
              <div class="adm-list">
                {players.map((p) => (
                  <div class="adm-row">
                    <div class="col" style={{ gap: 0 }}>
                      <b>
                        {p.name} {p.god ? '★' : ''} {p.dead ? '☠' : ''}
                      </b>
                      <span class="small muted">
                        {tn('hero', p.hero)} · {t('label.lvl')} {p.level} · {Math.round(p.hp)}/{Math.round(p.maxHp)} · #{p.id}
                      </span>
                    </div>
                    <div class="grow" />
                    <Btn small ghost onClick={() => setSayAs(p.id)} title={t('admin.sayAs')}>
                      💬
                    </Btn>
                    <Btn small ghost onClick={() => send({ k: 'god', on: !p.god, target: p.id })} title={t('admin.godOn')}>
                      ★
                    </Btn>
                    <Btn small ghost onClick={() => send({ k: 'teleport', x: S.self.value?.x ?? p.x, y: S.self.value?.y ?? p.y, target: p.id })} title={t('admin.pull')}>
                      ⇤
                    </Btn>
                    <Btn small ghost onClick={() => send({ k: 'slay', target: p.id })} title={t('admin.slay')}>
                      ☠
                    </Btn>
                    {!offline && (
                      <Btn small ghost onClick={() => send({ k: 'kick', target: p.id })} title={t('admin.kick')}>
                        ⛔
                      </Btn>
                    )}
                  </div>
                ))}
              </div>
              <div class="row wrap">
                <Sel width={140} value={sayAs === undefined ? '' : String(sayAs)} options={[{ id: '', label: t('admin.sayAsPick') }, ...players.map((p) => ({ id: String(p.id), label: p.name }))]} onChange={(v) => setSayAs(v ? Number(v) : undefined)} />
                <input
                  class="adm-text grow"
                  placeholder={t('admin.sayText')}
                  value={sayText}
                  onInput={(e) => setSayText((e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && sayAs !== undefined && sayText.trim()) {
                      send({ k: 'say', as: sayAs, text: sayText.trim() });
                      setSayText('');
                    }
                  }}
                />
                <Btn
                  small
                  disabled={sayAs === undefined || !sayText.trim()}
                  onClick={() => {
                    if (sayAs === undefined) return;
                    send({ k: 'say', as: sayAs, text: sayText.trim() });
                    setSayText('');
                  }}
                >
                  {t('admin.say')}
                </Btn>
              </div>
            </div>
          )}

          {tab === 'account' && (
            <div class="col" style={{ gap: 8 }}>
              <div class="row wrap">
                <span class="small muted">{t('label.fame')}</span>
                <Num value={fame} onChange={setFame} min={-1000000} max={1000000} step={100} width={90} />
                <Btn small onClick={() => send({ k: 'fame', amount: fame })} disabled={offline}>
                  {t('admin.give')}
                </Btn>
              </div>
              <div class="row wrap">
                <Sel value={unlockHero} options={[{ id: 'all', label: t('admin.allHeroes') }, ...HERO_IDS.map((id) => ({ id, label: tn('hero', id) }))]} onChange={setUnlockHero} />
                <Btn small onClick={() => send({ k: 'unlock', hero: unlockHero })}>
                  {t('admin.unlock')}
                </Btn>
              </div>
              <div class="row wrap">
                <span class="small muted">{t('label.heroes')}</span>
                <Num value={slots} onChange={setSlots} min={2} max={6} width={50} />
                <Btn small onClick={() => send({ k: 'slots', n: slots })} disabled={offline}>
                  {t('admin.setSlots')}
                </Btn>
              </div>
              <div class="row wrap">
                <Sel value={pet} options={Object.keys(PETS).map((id) => ({ id, label: tn('pet', id) }))} onChange={setPet} />
                <Num value={petLevel} onChange={setPetLevel} min={1} max={10} width={50} />
                <Btn small onClick={() => send({ k: 'pet', id: pet, level: petLevel })}>
                  {t('admin.give')}
                </Btn>
              </div>
              {offline && <div class="small muted">{t('admin.offlineNote')}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
